import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { EMPTY, exhaustMap, pipe } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { EInactivationChoiceOption } from '@settings/data-access';
import {
  TBindingSubscription,
  TInactivationAttempt,
  TInactivationHandoff,
} from './entities/inactivation-attempt.model';
import { InactivationAttemptService } from './infra/inactivation-attempt.service';

type TInactivationAttemptState = {
  /** The person the last attempt was for. */
  personId: string | null;
  /** The backend's answer to the last attempt. */
  attemptResult: TInactivationAttempt | null;
  /** The router option recorded for the current attempt. */
  choice: EInactivationChoiceOption | null;
  /** Option waiting to be opened by the subscriptions tab (consumed once). */
  handoff: TInactivationHandoff | null;
};

const initialState: TInactivationAttemptState = {
  personId: null,
  attemptResult: null,
  choice: null,
  handoff: null,
};

const OPS = ['attempt', 'choice'] as const;

const ERRORS = {
  attempt: 'PROFILE.INACTIVATION_ATTEMPT.ERRORS.ATTEMPT',
  choice: 'PROFILE.INACTIVATION_ATTEMPT.ERRORS.CHOICE',
} as const;

/**
 * Editar Perfil › "Ativar cliente": the inactivation router (modals 1 and 3).
 *
 * Flow: the user switches "Ativar cliente" off → `attempt(personId)`.
 * `allowed()` → the usual save goes on. `blocked()` → open modal 1
 * (`subscriptions().length === 1`) or 3, and before opening the flow of the
 * option picked, `recordChoice(option)`; "Manter ficha ativa" is
 * `recordChoice(EInactivationChoiceOption.KeepActive)` and the switch goes
 * back. After closing a plan, call `attempt(personId)` again: `allowed()`
 * then means "Deseja inativar a ficha agora?".
 *
 * Refusals (503 `dependency-unavailable`, 403, 404) raise the global alert
 * with the backend message (both ops are user actions); the screen reacts to
 * `attemptError()` / `choiceError()` and never offers "inativar mesmo assim".
 *
 * Pause, switch and close live in the profile's subscriptions tab: the router
 * leaves the option there with `handOff`, and the tab takes it once with
 * `takeHandoff`.
 *
 * A second call while one is in flight is ignored (double toggle, double
 * click).
 */
export const InactivationAttemptStore = signalStore(
  { providedIn: 'root' },
  withState<TInactivationAttemptState>(initialState),
  withReqState(...OPS),
  withComputed((store) => ({
    allowed: computed<boolean>(() => store.attemptResult()?.Allowed === true),
    blocked: computed<boolean>(() => store.attemptResult()?.Allowed === false),
    subscriptions: computed<TBindingSubscription[]>(
      () => store.attemptResult()?.Subscriptions ?? []
    ),
  })),
  withMethods(
    (
      store,
      service = inject(InactivationAttemptService),
      errorStore = inject(ErrorStore)
    ) => {
      const fail = (
        op: (typeof OPS)[number],
        error: HttpErrorResponse
      ): void => {
        patchState(
          store,
          setPropError(op, errorStore.getHttpErrorMessage(error) ?? ERRORS[op])
        );
        errorStore.setHttpError(error);
      };

      return {
        /** Asks whether the person file can be inactivated now. */
        attempt: rxMethod<string>(
          pipe(
            exhaustMap((personId) => {
              patchState(
                store,
                { personId, attemptResult: null, choice: null },
                setPropLoading('attempt'),
                setPropInit('choice')
              );
              return service.create(personId).pipe(
                tapResponse({
                  next: (attemptResult) =>
                    patchState(
                      store,
                      { attemptResult },
                      setPropLoaded('attempt')
                    ),
                  error: (error: HttpErrorResponse) => fail('attempt', error),
                })
              );
            })
          )
        ),

        /**
         * Records the router option for the current blocked attempt. Does
         * nothing when there is no blocked attempt. React to `choiceLoaded()`
         * to open the option's flow.
         */
        recordChoice: rxMethod<EInactivationChoiceOption>(
          pipe(
            exhaustMap((option) => {
              const personId = store.personId();
              const attemptId = store.attemptResult()?.AttemptId;
              if (!personId || !attemptId) {
                return EMPTY;
              }
              patchState(store, setPropLoading('choice'));
              return service.recordChoice(personId, attemptId, option).pipe(
                tapResponse({
                  next: () =>
                    patchState(
                      store,
                      { choice: option },
                      setPropLoaded('choice')
                    ),
                  error: (error: HttpErrorResponse) => fail('choice', error),
                })
              );
            })
          )
        ),

        /** Leaves the router's option for the subscriptions tab to open. */
        handOff: (handoff: TInactivationHandoff): void =>
          patchState(store, { handoff }),

        /** Takes the pending option (once): it is cleared as it is read. */
        takeHandoff: (): TInactivationHandoff | null => {
          const handoff = store.handoff();
          patchState(store, { handoff: null });
          return handoff;
        },

        /** Back to the untouched state (modal closed, profile left). */
        reset: (): void => patchState(store, initialState, setPropInit(...OPS)),
      };
    }
  )
);
